import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../../api/adminApi";
import type {
  EmailConfigDto,
  IssuedLicense,
  PlatformStats,
  SelfHostLicense,
  TenantOnboardingResponse,
  TenantUsage,
} from "../../api/types";
import { DataTable } from "../../components/DataTable";
import { EmailConfigForm } from "../../components/EmailConfigForm";

const PLAN_FILTER_OPTIONS = ["starter", "professional", "business", "enterprise"];

function StatCard({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="stat-card">
      <div className="stat-card-label">{label}</div>
      <div className="stat-card-value">{value}</div>
    </div>
  );
}

/** Each of these is its own sidebar link + route now (see Layout.tsx/router.tsx) rather than
 *  tabs under one cramped "Platform" page — a real admin panel's nav has this much structure. */
export function PlatformOverview() {
  return (
    <div>
      <div className="page-header">
        <h1>Overview</h1>
      </div>
      <OverviewTab />
    </div>
  );
}

export function PlatformTenants() {
  return (
    <div>
      <div className="page-header">
        <h1>Tenants</h1>
      </div>
      <TenantsTab />
    </div>
  );
}

export function PlatformEmailDefaults() {
  return (
    <div>
      <div className="page-header">
        <h1>Email defaults</h1>
      </div>
      <EmailDefaultsTab />
    </div>
  );
}

export function PlatformLicenses() {
  return (
    <div>
      <div className="page-header">
        <h1>Self-host licenses</h1>
      </div>
      <LicensesTab />
    </div>
  );
}

function OverviewTab() {
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminApi.platform
      .stats()
      .then(setStats)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load stats"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p>Loading...</p>;
  if (error) return <div className="error-text">{error}</div>;
  if (!stats) return null;

  return (
    <div>
      <div className="stat-grid">
        <StatCard label="Total tenants" value={stats.totalTenants} />
        <StatCard label="Total users" value={stats.totalUsers} />
        <StatCard label="Total reports" value={stats.totalReports} />
        <StatCard label="Executions (24h)" value={stats.executionsLast24h} />
      </div>

      <div className="page-panel">
        <h3>Tenants by status</h3>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {Object.entries(stats.byStatus).map(([status, count]) => (
            <span key={status} className="badge badge-draft">
              {status}: {count}
            </span>
          ))}
        </div>
      </div>

      <div className="page-panel">
        <h3>Recent signups</h3>
        <DataTable
          rows={stats.recentSignups}
          rowKey={(t) => t.id}
          emptyMessage="No signups yet."
          columns={[
            { key: "name", header: "Name" },
            { key: "slug", header: "Slug" },
            { key: "planTier", header: "Plan" },
            { key: "createdAt", header: "Signed up", render: (t) => new Date(t.createdAt).toLocaleString() },
          ]}
        />
      </div>
    </div>
  );
}

function TenantsTab() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<TenantUsage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerFullName, setOwnerFullName] = useState("");
  const [creating, setCreating] = useState(false);
  const [onboarding, setOnboarding] = useState<TenantOnboardingResponse | null>(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"ALL" | "ACTIVE" | "TRIAL" | "SUSPENDED">("ALL");
  const [planFilter, setPlanFilter] = useState<string>("ALL");
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "name">("newest");

  function load() {
    setLoading(true);
    adminApi.platform
      .tenants()
      .then(setTenants)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load tenants"))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  async function handleCreate() {
    if (!name.trim() || !slug.trim() || !ownerEmail.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const result = await adminApi.tenants.create({
        name: name.trim(),
        slug: slug.trim(),
        ownerEmail: ownerEmail.trim(),
        ownerFullName: ownerFullName.trim() || undefined,
      });
      setOnboarding(result);
      setName("");
      setSlug("");
      setOwnerEmail("");
      setOwnerFullName("");
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create tenant");
    } finally {
      setCreating(false);
    }
  }

  function copyCredentials() {
    if (onboarding) {
      navigator.clipboard
        ?.writeText(`Email: ${onboarding.ownerEmail}\nPassword: ${onboarding.ownerTemporaryPassword}`)
        .catch(() => undefined);
    }
  }

  async function toggleStatus(tenant: TenantUsage) {
    try {
      if (tenant.status === "SUSPENDED") {
        await adminApi.tenants.activate(tenant.id);
      } else {
        await adminApi.tenants.suspend(tenant.id);
      }
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update tenant status");
    }
  }

  const counts = useMemo(
    () => ({
      total: tenants.length,
      active: tenants.filter((t) => t.status === "ACTIVE").length,
      trial: tenants.filter((t) => t.status === "TRIAL").length,
      suspended: tenants.filter((t) => t.status === "SUSPENDED").length,
    }),
    [tenants]
  );

  const visibleTenants = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = tenants.filter((t) => {
      if (q && !t.name.toLowerCase().includes(q) && !t.slug.toLowerCase().includes(q)) return false;
      if (statusFilter !== "ALL" && t.status !== statusFilter) return false;
      if (planFilter !== "ALL" && (t.planTier || "").toLowerCase() !== planFilter) return false;
      return true;
    });
    rows = [...rows].sort((a, b) => {
      if (sortBy === "name") return a.name.localeCompare(b.name);
      const cmp = (a.createdAt || "").localeCompare(b.createdAt || "");
      return sortBy === "newest" ? -cmp : cmp;
    });
    return rows;
  }, [tenants, search, statusFilter, planFilter, sortBy]);

  return (
    <div>
      {error && <div className="error-text">{error}</div>}

      <div className="stat-grid">
        <StatCard label="Total tenants" value={counts.total} />
        <StatCard label="Active" value={counts.active} />
        <StatCard label="Trial" value={counts.trial} />
        <StatCard label="Suspended" value={counts.suspended} />
      </div>

      <div className="page-panel">
        <h3>New tenant</h3>
        <div className="form-grid">
          <div className="form-row">
            <label>Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="form-row">
            <label>Slug</label>
            <input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="acme" />
          </div>
          <div className="form-row">
            <label>Owner email</label>
            <input value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} placeholder="owner@acme.com" />
          </div>
          <div className="form-row">
            <label>Owner full name (optional)</label>
            <input value={ownerFullName} onChange={(e) => setOwnerFullName(e.target.value)} />
          </div>
        </div>
        <button type="button" className="btn-primary" onClick={handleCreate} disabled={creating}>
          {creating ? "Creating..." : "Create tenant"}
        </button>
      </div>

      {onboarding && (
        <div className="page-panel" style={{ borderColor: "#f59e0b" }}>
          <h4>Tenant created — hand these credentials to your client now</h4>
          <p className="help-text">
            This is the only time the password is shown. It can't be retrieved again — the client
            should change it after their first login.
          </p>
          <div className="code-block">
            Email: {onboarding.ownerEmail}
            {"\n"}Password: {onboarding.ownerTemporaryPassword}
            {"\n"}Tenant slug: {onboarding.tenant.slug}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button type="button" className="btn-secondary" onClick={copyCredentials}>
              Copy
            </button>
            <button type="button" className="btn-secondary" onClick={() => setOnboarding(null)}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="page-panel">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16, alignItems: "center" }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or slug..."
            style={{ flex: "1 1 240px" }}
          />
          {(["ALL", "ACTIVE", "TRIAL", "SUSPENDED"] as const).map((s) => (
            <button
              key={s}
              type="button"
              className={statusFilter === s ? "btn-primary" : "btn-secondary"}
              onClick={() => setStatusFilter(s)}
            >
              {s === "ALL" ? "All" : s.charAt(0) + s.slice(1).toLowerCase()}
            </button>
          ))}
          <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}>
            <option value="ALL">All plans</option>
            {PLAN_FILTER_OPTIONS.map((p) => (
              <option key={p} value={p}>
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </option>
            ))}
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value as typeof sortBy)}>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="name">Name</option>
          </select>
        </div>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <>
            <p className="help-text">
              Showing {visibleTenants.length} of {tenants.length} tenants.
            </p>
            <DataTable
              rows={visibleTenants}
              rowKey={(t) => t.id}
              emptyMessage="No tenants match your filters."
              columns={[
                { key: "name", header: "Name" },
                { key: "slug", header: "Slug" },
                { key: "planTier", header: "Plan" },
                {
                  key: "status",
                  header: "Status",
                  render: (t) => (
                    <span className={t.status === "ACTIVE" ? "badge badge-published" : "badge badge-archived"}>
                      {t.status}
                    </span>
                  ),
                },
                { key: "userCount", header: "Users" },
                { key: "reportCount", header: "Reports" },
                { key: "executions30d", header: "Executions (30d)" },
                {
                  key: "lastActiveAt",
                  header: "Last active",
                  render: (t) => (t.lastActiveAt ? new Date(t.lastActiveAt).toLocaleString() : "never"),
                },
                {
                  key: "actions",
                  header: "",
                  render: (t) => (
                    <div style={{ display: "flex", gap: 8 }}>
                      <button type="button" className="btn-secondary" onClick={() => toggleStatus(t)}>
                        {t.status === "SUSPENDED" ? "Activate" : "Suspend"}
                      </button>
                      <button type="button" className="btn-primary" onClick={() => navigate(`/admin/tenants/${t.id}`)}>
                        Manage
                      </button>
                    </div>
                  ),
                },
              ]}
            />
          </>
        )}
      </div>
    </div>
  );
}

function EmailDefaultsTab() {
  const [config, setConfig] = useState<EmailConfigDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  function load() {
    setLoading(true);
    adminApi.platform.emailConfig
      .get()
      .then(setConfig)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load email config"))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  return (
    <div className="page-panel">
      <h3>Platform default SMTP</h3>
      <p className="help-text">
        Used by any tenant that hasn't configured their own SMTP override under Settings &rarr; Email.
      </p>
      {error && <div className="error-text">{error}</div>}
      <EmailConfigForm
        config={config}
        loading={loading}
        onSave={async (body) => {
          const saved = await adminApi.platform.emailConfig.save(body);
          setConfig(saved);
        }}
        onTest={(to) => adminApi.platform.emailConfig.test(to)}
      />
    </div>
  );
}

function LicensesTab() {
  const [licenses, setLicenses] = useState<SelfHostLicense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [label, setLabel] = useState("");
  const [validDays, setValidDays] = useState(365);
  const [installationId, setInstallationId] = useState("");
  const [issuing, setIssuing] = useState(false);
  const [issued, setIssued] = useState<IssuedLicense | null>(null);

  function load() {
    setLoading(true);
    adminApi.platform.licenses
      .list()
      .then(setLicenses)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load licenses"))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  async function handleIssue() {
    if (!label.trim()) return;
    setIssuing(true);
    setError(null);
    try {
      const result = await adminApi.platform.licenses.issue(label.trim(), validDays, installationId);
      setIssued(result);
      setLabel("");
      setInstallationId("");
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to issue license");
    } finally {
      setIssuing(false);
    }
  }

  function downloadLicense(token: string, name: string) {
    const blob = new Blob([token + "\n"], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${name.replace(/[^a-z0-9]+/gi, "-").toLowerCase() || "reportforge"}.lic`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function copyKey() {
    if (issued) navigator.clipboard?.writeText(issued.issuedToken).catch(() => undefined);
  }

  async function handleRevoke(id: string) {
    try {
      await adminApi.platform.licenses.revoke(id);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to revoke license");
    }
  }

  return (
    <div>
      {error && <div className="error-text">{error}</div>}

      <div className="page-panel">
        <h3>Issue a self-host license</h3>
        <p className="help-text">
          Licenses a client to run the on-premise image (<code>samuelcheboitarus/report-forge-onprem</code>)
          on their own infrastructure. They upload the license file in the app's setup wizard, and later on
          its License page to renew. Paste the <strong>Installation ID</strong> from their setup screen to tie the
          license to their server; leave it blank for a license that works on any server.
        </p>
        <div className="form-grid">
          <div className="form-row">
            <label>Licensee label</label>
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Acme Corp - on-prem" />
          </div>
          <div className="form-row">
            <label>Valid for (days)</label>
            <input
              type="number"
              value={validDays}
              onChange={(e) => setValidDays(Number(e.target.value) || 0)}
            />
          </div>
        </div>
        <div className="form-row" style={{ maxWidth: 520 }}>
          <label>Installation ID (optional)</label>
          <input
            value={installationId}
            onChange={(e) => setInstallationId(e.target.value)}
            placeholder="123e4567-e89b-12d3-a456-426614174000"
            style={{ fontFamily: "var(--font-mono)" }}
          />
        </div>
        <button type="button" className="btn-primary" onClick={handleIssue} disabled={issuing}>
          {issuing ? "Issuing..." : "Issue license"}
        </button>
      </div>

      {issued && (
        <div className="page-panel" style={{ borderColor: "#f59e0b" }}>
          <h4>License issued</h4>
          <p className="help-text">
            Send the license file to the client - they upload it in the app. You can re-download it from the
            list below at any time.
          </p>
          <div className="code-block">{issued.issuedToken}</div>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button type="button" className="btn-primary" onClick={() => downloadLicense(issued.issuedToken, issued.licenseeLabel)}>
              Download .lic file
            </button>
            <button type="button" className="btn-secondary" onClick={copyKey}>
              Copy text
            </button>
            <button type="button" className="btn-secondary" onClick={() => setIssued(null)}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="page-panel">
        {loading ? (
          <p>Loading...</p>
        ) : (
          <DataTable
            rows={licenses}
            rowKey={(l) => l.id}
            emptyMessage="No licenses issued yet."
            columns={[
              { key: "licenseeLabel", header: "Licensee" },
              { key: "issuedAt", header: "Issued", render: (l) => new Date(l.issuedAt).toLocaleDateString() },
              { key: "expiresAt", header: "Expires", render: (l) => new Date(l.expiresAt).toLocaleDateString() },
              {
                key: "installationId",
                header: "Bound to",
                render: (l) =>
                  l.installationId ? (
                    <code title={l.installationId}>{l.installationId.slice(0, 8)}…</code>
                  ) : (
                    <span className="help-text">any server</span>
                  ),
              },
              {
                key: "revoked",
                header: "Status",
                render: (l) => (
                  <span className={l.revoked ? "badge badge-archived" : "badge badge-published"}>
                    {l.revoked ? "Revoked" : "Active"}
                  </span>
                ),
              },
              {
                key: "actions",
                header: "",
                render: (l) =>
                  !l.revoked && (
                    <div style={{ display: "flex", gap: 6 }}>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => downloadLicense((l as SelfHostLicense & { issuedToken: string }).issuedToken, l.licenseeLabel)}
                      >
                        Download .lic
                      </button>
                      <button type="button" className="btn-danger" onClick={() => handleRevoke(l.id)}>
                        Revoke
                      </button>
                    </div>
                  ),
              },
            ]}
          />
        )}
        <p className="help-text" style={{ marginTop: 12 }}>
          Revoking only removes a license from this list — verification happens fully offline inside
          the client's own running instance, so it does not invalidate a key already handed to a
          client. Only the expiry date is enforced there.
        </p>
      </div>
    </div>
  );
}
